// pages/POS/POSSales.js - COMPLETE WITH PRINT RECEIPT FUNCTION
import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Button,
  TextField,
  Grid,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  InputAdornment,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Divider,
  Tooltip,
  Alert,
  CircularProgress,
  Avatar,
  Stack,
  Autocomplete,
  TablePagination,
  Menu,
  ListItemIcon,
  ListItemText,
  Popover,
  Collapse,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormLabel
} from '@mui/material';
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  ArrowBack as ArrowBackIcon,
  Receipt as ReceiptIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  DateRange as DateRangeIcon,
  Person as PersonIcon,
  Payment as PaymentIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  ReceiptLong as ReceiptLongIcon,
  AttachMoney as AttachMoneyIcon,
  ShoppingCart as ShoppingCartIcon,
  CreditCard as CreditCardIcon,
  AccountBalanceWallet as WalletIcon,
  PhoneAndroid as MobileIcon,
  LocalAtm as CashIcon,
  Store as StoreIcon,
  Timeline as TimelineIcon,
  BarChart as BarChartIcon,
  LocalOffer as DiscountIcon,
  MoreVert as MoreVertIcon,
  Pending as PendingIcon,
  DoneAll as DoneAllIcon,
  Update as UpdateIcon,
  CalendarToday as CalendarIcon,
  Clear as ClearIcon,
  AccountBalance as ChequeIcon,
  Payment as OnlinePaymentIcon,
  Schedule as ScheduleIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  ErrorOutline as ErrorOutlineIcon,
  HourglassEmpty as HourglassEmptyIcon,
  LocalOffer as BulkIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { getPOS, getPOSStats, getCustomers, updatePOSStatus } from '../../services/api';
import DescriptionIcon from '@mui/icons-material/Description';

// Helper functions
const parseSerialNumbers = (serialData) => {
  if (!serialData) return [];
  if (Array.isArray(serialData)) return serialData;
  if (typeof serialData === 'string') {
    try {
      const parsed = JSON.parse(serialData);
      if (Array.isArray(parsed)) return parsed;
      return [serialData];
    } catch (e) {
      if (serialData.includes(',')) return serialData.split(',').map(s => s.trim()).filter(s => s);
      return [serialData];
    }
  }
  return [String(serialData)];
};

const formatCurrency = (amount) => {
  if (!amount && amount !== 0) return 'LKR 0.00';
  return new Intl.NumberFormat('en-LK', {
    style: 'currency',
    currency: 'LKR',
    minimumFractionDigits: 2
  }).format(amount);
};

const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDateOnly = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const calculateItemDiscounts = (items) => {
  if (!items || !Array.isArray(items)) return 0;
  return items.reduce((sum, item) => sum + (parseFloat(item.discount_amount) || 0), 0);
};

const calculateOriginalSubtotal = (items) => {
  if (!items || !Array.isArray(items)) return 0;
  return items.reduce((sum, item) => {
    const unitPrice = parseFloat(item.unit_price) || 0;
    const quantity = parseInt(item.quantity) || 1;
    return sum + (unitPrice * quantity);
  }, 0);
};

// ============ Print Receipt Function (Same as POS) ============
const printReceipt = (receiptData) => {
  // Create a new window with proper settings for server environment
  const receiptWindow = window.open('', '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');
  
  if (!receiptWindow) {
    alert('Please allow pop-ups to print receipts');
    return;
  }
  
  const itemsWithWarranty = receiptData.items?.filter(item => item.warranty_months > 0) || [];
  const itemsWithSerials = receiptData.items?.filter(item => 
    item.serial_numbers && item.serial_numbers.length > 0
  ) || [];
  
  const hasBulkSaleItems = receiptData.items?.some(item => item.is_bulk_sale) || false;
  const isCreditTransaction = receiptData.payment_method === 'credit';
  const isChequeTransaction = receiptData.payment_method === 'cheque';
  const isOnlineTransaction = ['online', 'mobile', 'bank_transfer'].includes(receiptData.payment_method);
  
  // Determine payment status
  const paymentStatus = receiptData.status || 
                       (isChequeTransaction ? 'pending' : 
                        isCreditTransaction ? (receiptData.status === 'completed' ? 'paid' : 'pending') : 
                        isOnlineTransaction ? (receiptData.online_payment_status || 'pending') : 'paid');
  
  const warrantyItemsWithDates = itemsWithWarranty.map(item => {
    const purchaseDate = new Date(receiptData.date);
    const warrantyEndDate = new Date(purchaseDate);
    warrantyEndDate.setMonth(warrantyEndDate.getMonth() + (item.warranty_months || 0));
    return {
      ...item,
      warranty_end_date: warrantyEndDate.toLocaleDateString()
    };
  });
  
  const bulkSaleItems = receiptData.items?.filter(item => item.is_bulk_sale) || [];
  const regularItems = receiptData.items?.filter(item => !item.is_bulk_sale) || [];
  
  const formatPaymentMethod = (method) => {
    switch(method) {
      case 'cash': return 'Cash';
      case 'card': return 'Card';
      case 'bank_transfer': return 'Bank Transfer';
      case 'mobile': return 'Online Payment';
      case 'online': return 'Online Payment';
      case 'credit': return 'Credit';
      case 'cheque': return 'Cheque';
      default: return method?.toUpperCase() || 'CASH';
    }
  };
  
  const safeNumber = (value) => {
    if (value === null || value === undefined) return 0;
    const num = parseFloat(value);
    return isNaN(num) ? 0 : num;
  };
  
  const formatNumber = (value) => {
    const num = safeNumber(value);
    return num.toFixed(2);
  };
  
  const displayNumber = receiptData.receipt_number || receiptData.pos_number || '';
  
  // Get the base URL for the logo - handle both local and server environments
  const baseUrl = window.location.origin;
  const logoUrl = `${baseUrl}/LOGOFile.png`;
  
  receiptWindow.document.write(`
    <!DOCTYPE html>
    <html>
    <head>
      <title>Invoice #${displayNumber}</title>
      <base href="${baseUrl}/">
      <style>
        @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600&family=Roboto+Mono:wght@400;500&display=swap');
        
        @media print {
          @page { 
            size: A4; 
            margin: 1cm; 
          }
          body { 
            margin: 0; 
            padding: 0; 
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          .no-print { 
            display: none !important; 
          }
          th {
            background: #1976d2 !important;
            color: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .status-paid {
            background: #2e7d32 !important;
            color: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .status-pending {
            background: #f57c00 !important;
            color: white !important;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
        }
        
        body { 
          font-family: 'Poppins', sans-serif; 
          font-size: 12px; 
          padding: 20px;
          max-width: 210mm;
          margin: 0 auto;
          background: white;
          color: #333;
        }
        
        .receipt-container {
          border: 1px solid #ddd;
          padding: 20px;
          border-radius: 8px;
        }
        
        .header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #1976d2;
        }
        
        .logo-container {
          flex: 0 0 auto;
        }
        
        .company-logo {
          height: 150px;
          width: auto;
          max-width: 300px;
          object-fit: contain;
        }
        
        .company-info {
          flex: 1;
          text-align: right;
        }
        
        .company-name {
          font-size: 18px;
          font-weight: 700;
          color: #1976d2;
          margin-bottom: 2px;
        }
        
        .store-info {
          font-size: 11px;
          color: #666;
          margin-top: 5px;
          line-height: 1.4;
        }
        
        .invoice-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 20px;
          padding: 10px;
          background: #f5f5f5;
          border-radius: 4px;
        }
        
        .invoice-title-section {
          display: flex;
          align-items: center;
          gap: 12px;
          flex-wrap: wrap;
        }
        
        .invoice-title {
          font-size: 18px;
          font-weight: 600;
          color: #1976d2;
        }
        
        .invoice-number {
          font-family: 'Roboto Mono', monospace;
          font-weight: 400;
          font-size: 16px;
        }
        
        .status-badge {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 600;
          margin-left: 10px;
        }
        
        .status-paid {
          background: #2e7d32;
          color: white;
        }
        
        .status-pending {
          background: #f57c00;
          color: white;
        }
        
        .customer-info {
          margin-bottom: 20px;
          padding: 10px;
          background: #f9f9f9;
          border-left: 4px solid #1976d2;
          border-radius: 4px;
        }
        
        table {
          width: 100%;
          border-collapse: collapse;
          margin: 15px 0;
        }
        
        th {
          background: #1976d2;
          color: white;
          padding: 10px;
          text-align: left;
          font-size: 12px;
        }
        
        td {
          padding: 8px 10px;
          border-bottom: 1px solid #ddd;
        }
        
        .right {
          text-align: right;
        }
        
        .total-row {
          background: #e3f2fd;
          font-weight: 700;
        }
        
        .warranty-info {
          font-size: 11px;
          color: #2e7d32;
          margin-top: 4px;
        }
        
        .serial-info {
          font-size: 11px;
          color: #1976d2;
          margin-top: 4px;
          font-family: 'Roboto Mono', monospace;
        }
        
        .cheque-info {
          background: #fff3e0;
          padding: 15px;
          border-radius: 4px;
          margin: 15px 0;
          border-left: 4px solid #f57c00;
        }
        
        .credit-info {
          background: #fff3e0;
          padding: 15px;
          border-radius: 4px;
          margin: 15px 0;
          border-left: 4px solid #f57c00;
        }
        
        .online-info {
          background: #f3e5f5;
          padding: 15px;
          border-radius: 4px;
          margin: 15px 0;
          border-left: 4px solid #9c27b0;
        }
        
        .settlement-info {
          background: #e8f5e8;
          padding: 15px;
          border-radius: 4px;
          margin: 15px 0;
          border-left: 4px solid #2e7d32;
        }
        
        .bulk-info {
          background: #fff3e0;
          padding: 15px;
          border-radius: 4px;
          margin: 15px 0;
          border-left: 4px solid #f57c00;
        }
        
        .footer {
          margin-top: 30px;
          text-align: center;
          font-size: 11px;
          color: #666;
          padding-top: 20px;
          border-top: 1px dashed #ddd;
        }
        
        .footer-message {
          font-size: 14px;
          font-weight: 600;
          color: #1976d2;
          margin-bottom: 10px;
        }
        
        .print-button {
          position: fixed;
          bottom: 20px;
          right: 20px;
          background: #1976d2;
          color: white;
          border: none;
          padding: 12px 24px;
          border-radius: 4px;
          font-size: 14px;
          cursor: pointer;
          font-weight: 600;
          z-index: 9999;
          box-shadow: 0 2px 10px rgba(0,0,0,0.3);
        }
        
        .print-button:hover {
          background: #1565c0;
        }
        
        .print-button:focus {
          outline: none;
        }
        
        /* Fallback for logo */
        .logo-fallback {
          display: none;
        }
      </style>
    </head>
    <body>
      <div class="receipt-container">
        <div class="header">
          <div class="logo-container">
            <img src="${logoUrl}" alt="Company Logo" class="company-logo" 
                 onerror="this.style.display='none'; document.querySelector('.logo-fallback').style.display='block';">
            <div class="logo-fallback" style="display:none;">
              <h2 style="color:#1976d2; margin:0;">Wasana</h2>
              <p style="margin:0; font-size:11px;">Electricals</p>
            </div>
          </div>
          <div class="company-info">
            <div class="company-name">Tha ⚡ Ruu</div>
            <div class="store-info">
              Hanwella road, Kirindiwela<br>
              Tel:  94 71 515 9372<br>
             
            </div>
          </div>
        </div>
        
        <div class="invoice-header">
          <div class="invoice-title-section">
            <span class="invoice-title">INVOICE</span>
            <span class="status-badge ${paymentStatus === 'paid' ? 'status-paid' : 'status-pending'}">
              ${paymentStatus === 'paid' ? 
                '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 6L9 17l-5-5"/></svg> PAID' : 
                '<svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 6v6l4 2"/></svg> PENDING'}
            </span>
            ${isCreditTransaction ? '<span style="background: #f57c00; color: white; padding: 4px 8px; border-radius: 4px; margin-left: 10px; font-size: 12px;">CREDIT SALE</span>' : ''}
            ${isChequeTransaction ? '<span style="background: #3f51b5; color: white; padding: 4px 8px; border-radius: 4px; margin-left: 10px; font-size: 12px;">CHEQUE PAYMENT</span>' : ''}
            ${isOnlineTransaction ? '<span style="background: #9c27b0; color: white; padding: 4px 8px; border-radius: 4px; margin-left: 10px; font-size: 12px;">ONLINE PAYMENT</span>' : ''}
          </div>
          <div>
            <span class="invoice-number">#${displayNumber}</span>
          </div>
        </div>
        
        <div class="customer-info">
          <table style="width: 100%; margin: 0; border: none;">
            <tr>
              <td style="border: none; padding: 4px;"><strong>Date:</strong> ${new Date(receiptData.date).toLocaleDateString()}</td>
              <td style="border: none; padding: 4px;"><strong>Time:</strong> ${new Date(receiptData.date).toLocaleTimeString()}</td>
            </tr>
            <tr>
              <td style="border: none; padding: 4px;"><strong>Customer:</strong> ${receiptData.customer || 'Walk-in Customer'}</td>
              <td style="border: none; padding: 4px;"><strong>Cashier:</strong> ${localStorage.getItem('erp_username') || 'POS User'}</td>
            </tr>
          </table>
        </div>
        
        ${isChequeTransaction && receiptData.cheque_details ? `
          <div class="cheque-info">
            <strong style="font-size: 14px;">🏦 CHEQUE DETAILS</strong><br><br>
            <strong>Cheque No:</strong> ${receiptData.cheque_details.chequeNumber}<br>
            <strong>Bank:</strong> ${receiptData.cheque_details.bankName} ${receiptData.cheque_details.branch ? `(${receiptData.cheque_details.branch})` : ''}<br>
            <strong>Cheque Date:</strong> ${new Date(receiptData.cheque_details.chequeDate).toLocaleDateString()}<br>
            <strong>Due Date:</strong> ${new Date(receiptData.cheque_details.dueDate).toLocaleDateString()}<br>
            ${receiptData.cheque_details.isPostDated ? '<strong>Type:</strong> Post-dated Cheque<br>' : ''}
            <strong>Status:</strong> <span style="background: #f57c00; color: white; padding: 2px 8px; border-radius: 4px;">${receiptData.cheque_status?.toUpperCase() || 'PENDING'}</span>
          </div>
        ` : ''}
        
        ${isCreditTransaction ? `
          <div class="credit-info">
            <strong>💰 CREDIT SALE</strong><br><br>
            ${receiptData.status === 'completed' && receiptData.settlement_method ? `
              <strong>Settlement Method:</strong> ${receiptData.settlement_method.toUpperCase()}<br>
              <strong>Settlement Date:</strong> ${receiptData.settlement_date ? new Date(receiptData.settlement_date).toLocaleDateString() : 'N/A'}<br>
              ${receiptData.settlement_reference ? `<strong>Reference:</strong> ${receiptData.settlement_reference}<br>` : ''}
              <strong>Status:</strong> <span style="background: #2e7d32; color: white; padding: 2px 8px; border-radius: 4px;">PAID</span>
            ` : `
              <strong>Due Date:</strong> ${receiptData.due_date || 'N/A'}<br>
              <strong>Reference:</strong> ${receiptData.credit_reference || 'N/A'}<br>
              <strong>Status:</strong> <span style="background: #f57c00; color: white; padding: 2px 8px; border-radius: 4px;">PENDING</span>
            `}
          </div>
        ` : ''}
        
        ${isOnlineTransaction && receiptData.online_payment_reference ? `
          <div class="online-info">
            <strong>📱 ONLINE PAYMENT DETAILS</strong><br><br>
            <strong>Reference:</strong> ${receiptData.online_payment_reference}<br>
            <strong>Status:</strong> <span style="background: ${receiptData.online_payment_status === 'completed' ? '#2e7d32' : '#f57c00'}; color: white; padding: 2px 8px; border-radius: 4px;">
              ${receiptData.online_payment_status?.toUpperCase() || 'PENDING'}
            </span>
          </div>
        ` : ''}
        
        ${hasBulkSaleItems ? `
          <div class="bulk-info">
            <strong>🔥 BULK SALE PACKAGE</strong><br><br>
            <strong>Total Items:</strong> ${bulkSaleItems.reduce((sum, item) => sum + item.quantity, 0)}<br>
            <strong>Products:</strong> ${bulkSaleItems.length}<br>
            <strong>Package Discount:</strong> ${receiptData.bulk_discount_percentage || 0}% off
          </div>
        ` : ''}
        
        <table>
          <thead>
            <tr>
              <th style="width: 50%;">Item Description</th>
              <th style="width: 10%;">Qty</th>
              <th style="width: 20%;" class="right">Unit Price</th>
              <th style="width: 20%;" class="right">Total</th>
            </tr>
          </thead>
          <tbody>
            ${regularItems.map(item => {
              const warrantyItem = warrantyItemsWithDates.find(w => w.product_id === item.product_id);
              const serialItem = itemsWithSerials.find(s => s.product_id === item.product_id);
              
              let warrantyDisplay = '';
              if (warrantyItem && warrantyItem.warranty_months > 0) {
                warrantyDisplay = `<div class="warranty-info">🔰 Warranty: ${warrantyItem.warranty_months} months (until ${warrantyItem.warranty_end_date})</div>`;
                if (warrantyItem.warranty_terms) {
                  warrantyDisplay += `<div class="warranty-info" style="color: #666;">📋 Terms: ${warrantyItem.warranty_terms}</div>`;
                }
              }
              
              let serialDisplay = '';
              if (serialItem && serialItem.serial_numbers && serialItem.serial_numbers.length > 0) {
                serialDisplay = `<div class="serial-info">🔢 Serial: ${serialItem.serial_numbers.join(', ')}</div>`;
              }
              
              return `
                <tr>
                  <td>
                    <strong>${item.product_name || item.name}</strong>
                    ${item.product_code ? `<br><span style="color: #666; font-size: 11px;">Code: ${item.product_code}</span>` : ''}
                    ${item.discount_percentage > 0 ? `<br><span style="color: #f57c00; font-size: 11px;">Discount: ${item.discount_percentage}%</span>` : ''}
                    ${warrantyDisplay}
                    ${serialDisplay}
                  </td>
                  <td>${item.quantity}</td>
                  <td class="right">${formatNumber(item.unit_price)}</td>
                  <td class="right"><strong>${formatNumber(item.line_total)}</strong></td>
                </tr>
              `;
            }).join('')}
            
            ${bulkSaleItems.length > 0 ? `
              <tr style="background: #fff3e0;">
                <td colspan="4" style="padding: 10px;">
                  <strong style="color: #f57c00;">🔥 BUNDLE PACKAGE ITEMS</strong>
                  <table style="width: 100%; margin-top: 10px; background: white;">
                    <thead>
                      <tr>
                        <th style="width: 80%;">Items Included</th>
                        <th style="width: 20%;" class="right">Quantity</th>
                      </tr>
                    </thead>
                    <tbody>
                      ${bulkSaleItems.map(item => `
                        <tr>
                          <td>${item.product_name || item.name}</td>
                          <td class="right">${item.quantity}</td>
                        </tr>
                      `).join('')}
                    </tbody>
                  </table>
                </td>
              </tr>
            ` : ''}
          </tbody>
        </table>
        
        <table style="width: 100%; margin-top: 20px;">
          <tr>
            <td style="border: none; text-align: right; width: 80%;"><strong>Subtotal:</strong></td>
            <td style="border: none; text-align: right; width: 20%;">${formatNumber(receiptData.subtotal)}</td>
          </tr>
          ${safeNumber(receiptData.discount) > 0 ? `
            <tr>
              <td style="border: none; text-align: right;"><strong>Discount:</strong></td>
              <td style="border: none; text-align: right; color: #f57c00;">-${formatNumber(receiptData.discount)}</td>
            </tr>
          ` : ''}
          ${safeNumber(receiptData.tax) > 0 ? `
            <tr>
              <td style="border: none; text-align: right;"><strong>Tax:</strong></td>
              <td style="border: none; text-align: right;">${formatNumber(receiptData.tax)}</td>
            </tr>
          ` : ''}
          <tr class="total-row">
            <td style="border: none; text-align: right; font-size: 16px;"><strong>TOTAL:</strong></td>
            <td style="border: none; text-align: right; font-size: 16px;"><strong>${formatNumber(receiptData.total)}</strong></td>
          </tr>
          <tr>
            <td style="border: none; text-align: right;"><strong>Payment Method:</strong></td>
            <td style="border: none; text-align: right;"><strong>${formatPaymentMethod(receiptData.payment_method)}</strong></td>
          </tr>
          ${!isCreditTransaction && !isChequeTransaction && !isOnlineTransaction && paymentStatus === 'paid' ? `
            <tr>
              <td style="border: none; text-align: right;"><strong>Amount Paid:</strong></td>
              <td style="border: none; text-align: right;">${formatNumber(receiptData.paid)}</td>
            </tr>
            <tr>
              <td style="border: none; text-align: right;"><strong>Change:</strong></td>
              <td style="border: none; text-align: right; color: #2e7d32;">${formatNumber(receiptData.change)}</td>
            </tr>
          ` : ''}
        </table>
        
        <div class="footer">
          <div class="footer-message">Thank you for choosing Tha ⚡ Ruu – We value your trust!</div>
          <p style="font-size: 12px; font-weight: 700;">Your End-to-End Electrical Partner</p>        
          <p style="font-size: 12px; font-weight: 600;">Affordable. Reliable. Guaranteed.</p>
          <hr style="border: none; border-top: 1px dashed #ddd; margin: 15px 0;">
          <p style="font-size: 10px;">POS System Developed by Jcodelabs.lk | Contact: 94 74 164 0774</p>
          <p style="font-size: 10px;">Invoice #${displayNumber} | ${new Date().toLocaleString()}</p>
        </div>
      </div>
      
      <button class="print-button no-print" onclick="window.print(); setTimeout(() => window.close(), 1000);">
        🖨️ Print Receipt
      </button>
    </body>
    </html>
  `);
  
  receiptWindow.document.close();
  receiptWindow.focus();
};
// ================================================================

// ============ Proper bulk sale detection ============
const hasBulkSaleItems = (items) => {
  if (!items || !Array.isArray(items)) return false;
  return items.some(item => {
    if (item.is_bulk_sale === true) return true;
    if (item.is_bulk_sale === 'true') return true;
    if (item.is_bulk_sale === 1) return true;
    if (item.is_bulk_sale === '1') return true;
    if (item.is_bulk_sale && item.is_bulk_sale.toString().toLowerCase() === 'true') return true;
    return false;
  });
};

const calculateBulkDiscountPercentage = (items) => {
  if (!items || !Array.isArray(items)) return 0;
  const bulkItems = items.filter(item => {
    if (item.is_bulk_sale === true) return true;
    if (item.is_bulk_sale === 'true') return true;
    if (item.is_bulk_sale === 1) return true;
    if (item.is_bulk_sale === '1') return true;
    return false;
  });
  if (bulkItems.length === 0) return 0;
  
  let totalOriginal = 0;
  let totalDiscounted = 0;
  
  bulkItems.forEach(item => {
    const quantity = parseInt(item.quantity) || 1;
    const originalPrice = parseFloat(item.original_price) || parseFloat(item.unit_price) || 0;
    const discountedPrice = parseFloat(item.line_total) / quantity;
    totalOriginal += originalPrice * quantity;
    totalDiscounted += discountedPrice * quantity;
  });
  
  if (totalOriginal === 0) return 0;
  return ((totalOriginal - totalDiscounted) / totalOriginal * 100).toFixed(1);
};

// ============ Payment status details ============
const getPaymentStatusDetails = (transaction) => {
  const { payment_method, status, cheque_status, online_payment_status } = transaction;
  
  if (payment_method === 'cash' || payment_method === 'card') {
    return {
      status: 'completed',
      color: 'success',
      icon: <CheckCircleIcon fontSize="small" />,
      label: 'Completed'
    };
  }
  
  if (payment_method === 'credit') {
    return {
      status: status || 'pending',
      color: status === 'completed' ? 'success' : 'warning',
      icon: status === 'completed' ? <CheckCircleIcon fontSize="small" /> : <PendingIcon fontSize="small" />,
      label: status === 'completed' ? 'Paid' : 'Pending'
    };
  }
  
  if (payment_method === 'cheque') {
    const chequeStatus = cheque_status || status || 'pending';
    const statusMap = {
      pending: { color: 'warning', icon: <HourglassEmptyIcon fontSize="small" />, label: 'Pending Clearance' },
      cleared: { color: 'success', icon: <CheckCircleIcon fontSize="small" />, label: 'Cleared' },
      bounced: { color: 'error', icon: <ErrorOutlineIcon fontSize="small" />, label: 'Bounced' },
      cancelled: { color: 'error', icon: <CancelIcon fontSize="small" />, label: 'Cancelled' },
      completed: { color: 'success', icon: <CheckCircleIcon fontSize="small" />, label: 'Completed' }
    };
    return statusMap[chequeStatus] || { color: 'warning', icon: <ScheduleIcon fontSize="small" />, label: 'Processing' };
  }
  
  if (payment_method === 'online' || payment_method === 'mobile' || payment_method === 'bank_transfer') {
    const onlineStatus = online_payment_status || status || 'pending';
    const statusMap = {
      pending: { color: 'warning', icon: <HourglassEmptyIcon fontSize="small" />, label: 'Pending' },
      completed: { color: 'success', icon: <CheckCircleIcon fontSize="small" />, label: 'Completed' },
      failed: { color: 'error', icon: <ErrorOutlineIcon fontSize="small" />, label: 'Failed' },
      refunded: { color: 'info', icon: <ReceiptIcon fontSize="small" />, label: 'Refunded' }
    };
    return statusMap[onlineStatus] || { color: 'warning', icon: <ScheduleIcon fontSize="small" />, label: 'Processing' };
  }
  
  return {
    status: status || 'completed',
    color: status === 'completed' ? 'success' : status === 'pending' ? 'warning' : 'default',
    icon: status === 'completed' ? <CheckCircleIcon fontSize="small" /> : 
          status === 'pending' ? <PendingIcon fontSize="small" /> : null,
    label: (status || 'completed').toUpperCase()
  };
};

const paymentIcons = {
  cash: <CashIcon fontSize="small" />,
  card: <CreditCardIcon fontSize="small" />,
  credit: <WalletIcon fontSize="small" />,
  mobile: <MobileIcon fontSize="small" />,
  online: <OnlinePaymentIcon fontSize="small" />,
  bank_transfer: <WalletIcon fontSize="small" />,
  cheque: <ChequeIcon fontSize="small" />
};

const paymentColors = {
  cash: 'success',
  card: 'primary',
  credit: 'warning',
  mobile: 'info',
  online: 'secondary',
  bank_transfer: 'info',
  cheque: 'info'
};

const POSSales = () => {
  const [transactions, setTransactions] = useState([]);
  const [filteredTransactions, setFilteredTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [stats, setStats] = useState({});
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [dateFilter, setDateFilter] = useState('all');
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [chequeStatusFilter, setChequeStatusFilter] = useState('all');
  const [onlineStatusFilter, setOnlineStatusFilter] = useState('all');
  const [bulkSaleFilter, setBulkSaleFilter] = useState('all');
  const [customers, setCustomers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedTransactionForMenu, setSelectedTransactionForMenu] = useState(null);
  const [expandedFilters, setExpandedFilters] = useState(false);
  
  // ============ Credit Payment Completion Dialog ============
  const [creditPaymentDialogOpen, setCreditPaymentDialogOpen] = useState(false);
  const [creditPaymentData, setCreditPaymentData] = useState({
    paymentMethod: 'cash',
    reference: '',
    notes: ''
  });
  // ==========================================================
  
  // Custom date range
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [dateRangeAnchorEl, setDateRangeAnchorEl] = useState(null);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchTransactions();
    fetchStats();
    fetchCustomers();
  }, []);

  useEffect(() => {
    filterTransactions();
  }, [transactions, searchTerm, dateFilter, paymentFilter, statusFilter, 
      chequeStatusFilter, onlineStatusFilter, bulkSaleFilter, selectedCustomer, 
      customStartDate, customEndDate]);

  const fetchTransactions = async () => {
    try {
      setLoading(true);
      const response = await getPOS();
      
      if (response && response.success) {
        const transactionsData = response.data || [];
        
        const processedTransactions = transactionsData.map(tx => {
          let status = tx.status;
          let cheque_status = tx.cheque_status;
          let online_payment_status = tx.online_payment_status;
          
          if (!tx.status) {
            if (tx.payment_method === 'cash' || tx.payment_method === 'card') {
              status = 'completed';
            } else if (tx.payment_method === 'credit') {
              status = 'pending';
            } else if (tx.payment_method === 'cheque') {
              status = 'pending';
              cheque_status = tx.cheque_status || 'pending';
            } else if (tx.payment_method === 'online' || tx.payment_method === 'mobile' || tx.payment_method === 'bank_transfer') {
              status = tx.status || 'pending';
              online_payment_status = tx.online_payment_status || tx.status || 'pending';
            }
          }
          
          const hasBulk = hasBulkSaleItems(tx.items);
          
          let chequeDetails = tx.cheque_details;
          if (chequeDetails && typeof chequeDetails === 'string') {
            try {
              chequeDetails = JSON.parse(chequeDetails);
            } catch (e) {
              console.error('Error parsing cheque_details:', e);
            }
          }
          
          // Parse notes to extract settlement information if needed
          let settlement_method = tx.settlement_method;
          let settlement_reference = tx.settlement_reference;
          let settlement_date = tx.settlement_date;
          
          // If settlement data isn't directly available but might be in notes, try to extract it
          if (!settlement_method && tx.notes && tx.notes.includes('Credit payment settled via')) {
            const notesMatch = tx.notes.match(/Credit payment settled via ([A-Z_]+)(?: \(Ref: (.*?)\))?/);
            if (notesMatch) {
              settlement_method = notesMatch[1];
              settlement_reference = notesMatch[2] || '';
              settlement_date = tx.updated_at || tx.transaction_date;
            }
          }
          
          return {
            ...tx,
            id: tx.id || Math.random().toString(36).substr(2, 9),
            items: Array.isArray(tx.items) ? tx.items : [],
            status: status || 'completed',
            cheque_status: cheque_status || null,
            online_payment_status: online_payment_status || null,
            cheque_details: chequeDetails || null,
            has_bulk_sale: hasBulk,
            bulk_discount_percentage: hasBulk ? calculateBulkDiscountPercentage(tx.items) : 0,
            item_discount_total: calculateItemDiscounts(tx.items),
            original_subtotal: calculateOriginalSubtotal(tx.items),
            // ============ Preserve settlement data ============
            settlement_method: settlement_method || null,
            settlement_reference: settlement_reference || null,
            settlement_date: settlement_date || null
            // ==================================================
          };
        });
        setTransactions(processedTransactions);
        setError('');
      } else {
        setError(response?.message || 'Failed to load transactions');
      }
    } catch (err) {
      console.error('Error fetching POS transactions:', err);
      setError(err.message || 'Failed to load transactions');
    } finally {
      setLoading(false);
    }
  };

  const fetchCustomers = async () => {
    try {
      const response = await getCustomers();
      if (response && response.success) {
        setCustomers(response.data || []);
      }
    } catch (err) {
      console.error('Error fetching customers:', err);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await getPOSStats();
      if (response && response.success) {
        setStats(response.data || {});
      }
    } catch (err) {
      console.error('Error fetching POS stats:', err);
    }
  };

  const updateTransactionStatus = async (transactionId, newStatus, additionalData = {}) => {
    try {
      setLoading(true);
      
      // Ensure settlement data is properly formatted
      const updateData = { 
        status: newStatus,
        ...additionalData
      };
      
      // If this is a credit payment completion, make sure settlement data is included
      if (additionalData.settlement_method) {
        updateData.settlement_method = additionalData.settlement_method;
        updateData.settlement_reference = additionalData.settlement_reference || '';
        updateData.settlement_date = additionalData.settlement_date || new Date().toISOString();
      }
      
      console.log('Updating transaction with data:', updateData);
      
      const response = await updatePOSStatus(transactionId, updateData);
      
      if (response && response.success) {
        // Immediately update the local state with the settlement data
        setTransactions(prev => prev.map(tx => 
          tx.id === transactionId ? { 
            ...tx, 
            status: newStatus, 
            ...additionalData,
            // Ensure settlement data is explicitly set
            settlement_method: additionalData.settlement_method || tx.settlement_method,
            settlement_reference: additionalData.settlement_reference || tx.settlement_reference,
            settlement_date: additionalData.settlement_date || tx.settlement_date
          } : tx
        ));
        
        setSuccess('Status updated successfully');
        setTimeout(() => setSuccess(''), 3000);
        
        if (selectedTransaction && selectedTransaction.id === transactionId) {
          setSelectedTransaction(prev => ({ 
            ...prev, 
            status: newStatus, 
            ...additionalData 
          }));
        }
        
        handleCloseMenu();
        return true;
      } else {
        setError(response?.message || 'Failed to update status');
        return false;
      }
    } catch (err) {
      console.error('Error updating transaction status:', err);
      setError(err.message || 'Failed to update status');
      return false;
    } finally {
      setLoading(false);
    }
  };

  // ============ Handle Credit Payment Completion ============
  const handleCreditPaymentCompletion = async () => {
    if (!selectedTransactionForMenu) return;
    
    const settlementMethod = creditPaymentData.paymentMethod;
    const settlementReference = creditPaymentData.reference;
    const settlementDate = new Date().toISOString();
    const notes = `Credit payment settled via ${settlementMethod.toUpperCase()}${settlementReference ? ` (Ref: ${settlementReference})` : ''} - ${creditPaymentData.notes || 'Payment received'}`;
    
    console.log('Saving credit payment with settlement method:', settlementMethod);
    
    const success = await updateTransactionStatus(
      selectedTransactionForMenu.id, 
      'completed', 
      { 
        notes: notes,
        settlement_method: settlementMethod,
        settlement_reference: settlementReference,
        settlement_date: settlementDate
      }
    );
    
    if (success) {
      setCreditPaymentDialogOpen(false);
      // Reset the form but keep default payment method as 'cash'
      setCreditPaymentData({
        paymentMethod: 'cash',
        reference: '',
        notes: ''
      });
      
      // Force a refresh of the transaction data to ensure settlement data is loaded
      setTimeout(() => {
        fetchTransactions();
      }, 500);
    }
  };
  // ===========================================================

  // ============ Open Credit Payment Dialog with current data ============
  const handleOpenCreditPaymentDialog = () => {
    // Reset to default values when opening
    setCreditPaymentData({
      paymentMethod: 'cash',
      reference: '',
      notes: ''
    });
    setCreditPaymentDialogOpen(true);
  };
  // =======================================================================

  // ============ Handle Print Receipt ============
  const handlePrintReceipt = (transaction) => {
    // Parse items to ensure serial numbers are properly formatted
    const items = (transaction.items || []).map(item => ({
      ...item,
      serial_numbers: parseSerialNumbers(item.serial_numbers)
    }));

    // Prepare receipt data in the same format as POS
    const receiptData = {
      ...transaction,
      // Map transaction fields to receipt fields
      date: transaction.transaction_date || transaction.created_at,
      customer: transaction.customer_name || 'Walk-in Customer',
      items: items,
      subtotal: parseFloat(transaction.subtotal) || 0,
      discount: parseFloat(transaction.discount_amount) || 0,
      tax: parseFloat(transaction.tax_amount) || 0,
      total: parseFloat(transaction.total_amount) || 0,
      paid: parseFloat(transaction.amount_paid) || parseFloat(transaction.total_amount) || 0,
      change: parseFloat(transaction.change_amount) || 0,
      receipt_number: transaction.pos_number || transaction.receipt_number || '',
      pos_number: transaction.pos_number || transaction.receipt_number || '',
      payment_method: transaction.payment_method || 'cash',
      status: transaction.status || 
             (transaction.payment_method === 'cash' || transaction.payment_method === 'card' ? 'completed' : 'pending'),
      cheque_details: transaction.cheque_details || null,
      cheque_status: transaction.cheque_status || null,
      online_payment_status: transaction.online_payment_status || null,
      online_payment_reference: transaction.online_payment_reference || null,
      due_date: transaction.due_date || null,
      credit_reference: transaction.credit_reference || null,
      settlement_method: transaction.settlement_method || null,
      settlement_reference: transaction.settlement_reference || null,
      settlement_date: transaction.settlement_date || null,
      bulk_discount_percentage: transaction.bulk_discount_percentage || 0,
      // Ensure boolean values are properly handled
      has_bulk_sale: transaction.has_bulk_sale || hasBulkSaleItems(items)
    };
    
    printReceipt(receiptData);
  };
  // ===============================================

  const updateChequeStatus = async (transactionId, chequeStatus, notes = '') => {
    const currentTransaction = transactions.find(tx => tx.id === transactionId);
    const currentNotes = currentTransaction?.notes || '';
    const updatedNotes = notes ? 
      (currentNotes ? `${currentNotes}\nCheque ${chequeStatus}: ${notes}` : `Cheque ${chequeStatus}: ${notes}`) 
      : currentNotes;
    
    return updateTransactionStatus(transactionId, 
      chequeStatus === 'cleared' ? 'completed' : 'pending',
      { cheque_status: chequeStatus, notes: updatedNotes }
    );
  };

  const updateOnlinePaymentStatus = async (transactionId, onlineStatus, notes = '') => {
    const currentTransaction = transactions.find(tx => tx.id === transactionId);
    const currentNotes = currentTransaction?.notes || '';
    const updatedNotes = notes ? 
      (currentNotes ? `${currentNotes}\nOnline payment ${onlineStatus}: ${notes}` : `Online payment ${onlineStatus}: ${notes}`) 
      : currentNotes;
    
    return updateTransactionStatus(transactionId,
      onlineStatus === 'completed' ? 'completed' : 'pending',
      { online_payment_status: onlineStatus, notes: updatedNotes }
    );
  };

  const handleOpenMenu = (event, transaction) => {
    setAnchorEl(event.currentTarget);
    setSelectedTransactionForMenu(transaction);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setSelectedTransactionForMenu(null);
  };

  // Date filtering functions
  const isDateToday = (dateString) => {
    const date = new Date(dateString);
    const today = new Date();
    return date.getDate() === today.getDate() &&
           date.getMonth() === today.getMonth() &&
           date.getFullYear() === today.getFullYear();
  };

  const isDateYesterday = (dateString) => {
    const date = new Date(dateString);
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return date.getDate() === yesterday.getDate() &&
           date.getMonth() === yesterday.getMonth() &&
           date.getFullYear() === yesterday.getFullYear();
  };

  const isWithinLastWeek = (dateString) => {
    const date = new Date(dateString);
    const weekAgo = new Date();
    weekAgo.setDate(weekAgo.getDate() - 7);
    return date >= weekAgo;
  };

  const isWithinLastMonth = (dateString) => {
    const date = new Date(dateString);
    const monthAgo = new Date();
    monthAgo.setMonth(monthAgo.getMonth() - 1);
    return date >= monthAgo;
  };

  const isWithinCustomRange = (dateString, startDate, endDate) => {
    if (!startDate || !endDate) return true;
    try {
      const date = new Date(dateString);
      const start = new Date(startDate);
      const end = new Date(endDate);
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);
      return date >= start && date <= end;
    } catch (error) {
      return true;
    }
  };

  const handleDateRangeClick = (event) => {
    setDateRangeAnchorEl(event.currentTarget);
  };

  const handleDateRangeClose = () => {
    setDateRangeAnchorEl(null);
  };

  const handleApplyCustomDateRange = () => {
    if (customStartDate && customEndDate) {
      setDateFilter('custom');
    }
    handleDateRangeClose();
  };

  const handleClearCustomDateRange = () => {
    setCustomStartDate('');
    setCustomEndDate('');
    if (dateFilter === 'custom') {
      setDateFilter('all');
    }
    handleDateRangeClose();
  };

  const filterTransactions = () => {
    let filtered = [...transactions];

    if (searchTerm) {
      filtered = filtered.filter(transaction =>
        transaction.pos_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        transaction.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (transaction.items && transaction.items.some(item =>
          item.product_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          item.product_code?.toLowerCase().includes(searchTerm.toLowerCase())
        )) ||
        (transaction.cheque_details?.chequeNumber?.toLowerCase().includes(searchTerm.toLowerCase())) ||
        (transaction.cheque_details?.bankName?.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    switch (dateFilter) {
      case 'today':
        filtered = filtered.filter(t => isDateToday(t.transaction_date));
        break;
      case 'yesterday':
        filtered = filtered.filter(t => isDateYesterday(t.transaction_date));
        break;
      case 'week':
        filtered = filtered.filter(t => isWithinLastWeek(t.transaction_date));
        break;
      case 'month':
        filtered = filtered.filter(t => isWithinLastMonth(t.transaction_date));
        break;
      case 'custom':
        filtered = filtered.filter(t => isWithinCustomRange(t.transaction_date, customStartDate, customEndDate));
        break;
      default: break;
    }

    if (paymentFilter !== 'all') {
      filtered = filtered.filter(t => t.payment_method === paymentFilter);
    }

    if (statusFilter !== 'all') {
      if (statusFilter === 'refunded') {
        filtered = filtered.filter(t => t.is_refund);
      } else {
        filtered = filtered.filter(t => t.status === statusFilter && !t.is_refund);
      }
    }

    if (chequeStatusFilter !== 'all') {
      filtered = filtered.filter(t => t.payment_method === 'cheque' && t.cheque_status === chequeStatusFilter);
    }

    if (onlineStatusFilter !== 'all') {
      filtered = filtered.filter(t =>
        (t.payment_method === 'online' || t.payment_method === 'mobile' || t.payment_method === 'bank_transfer') &&
        t.online_payment_status === onlineStatusFilter
      );
    }

    if (bulkSaleFilter !== 'all') {
      const hasBulk = bulkSaleFilter === 'yes';
      filtered = filtered.filter(t => t.has_bulk_sale === hasBulk);
    }

    if (selectedCustomer) {
      filtered = filtered.filter(t => t.customer_id === selectedCustomer.id);
    }

    filtered.sort((a, b) => new Date(b.transaction_date) - new Date(a.transaction_date));
    setFilteredTransactions(filtered);
    setPage(0);
  };

  const handleViewTransaction = (transaction) => {
    setSelectedTransaction(transaction);
    setViewDialogOpen(true);
  };

  const handleExportCSV = () => {
  if (filteredTransactions.length === 0) return;
  
  const headers = [
    'Receipt #', 'Date', 'Customer', 'Items Count', 'Bulk Sale',
    'Bulk Discount %', 'Subtotal', 'Total', 'Payment Method',
    'Payment Status', 'Cheque Status', 'Cheque Number', 'Cheque Bank',
    'Online Status', 'Cashier', 'Settlement Method', 'Settlement Reference'
  ];
  
  const csvData = filteredTransactions.map(tx => {
    // Fix date formatting - remove the extra space and time part
    const formattedDate = tx.transaction_date ? new Date(tx.transaction_date).toLocaleDateString('en-GB') : '';
    
    // Only show online status for online transactions
    const onlineStatus = (tx.payment_method === 'online' || tx.payment_method === 'mobile' || tx.payment_method === 'bank_transfer') 
      ? (tx.online_payment_status || 'pending') 
      : '';
    
    return [
      tx.pos_number || '',
      formattedDate,
      tx.customer_name || '',
      tx.items?.length || 0,
      tx.has_bulk_sale ? 'Yes' : 'No',
      tx.bulk_discount_percentage || 0,
      tx.subtotal || 0,
      tx.total_amount || 0,
      tx.payment_method || '',
      getPaymentStatusDetails(tx).label,
      tx.cheque_status || '',
      tx.cheque_details?.chequeNumber || '',
      tx.cheque_details?.bankName || '',
      onlineStatus, // Fixed: Only show for online transactions
      tx.user_name || '',
      tx.settlement_method || '',
      tx.settlement_reference || ''
    ];
  });

  const csvContent = [
    headers.join(','),
    ...csvData.map(row => row.map(cell => {
      // Handle cells that might contain commas
      if (typeof cell === 'string' && cell.includes(',')) {
        return `"${cell}"`;
      }
      return cell;
    }).join(','))
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `pos_sales_${new Date().toISOString().split('T')[0]}.csv`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.URL.revokeObjectURL(url);
  
  setSuccess('CSV exported successfully');
  setTimeout(() => setSuccess(''), 3000);
};

  // Calculate statistics
  const calculateStats = () => {
    const today = filteredTransactions.filter(tx => isDateToday(tx.transaction_date));
    
    const totalSales = filteredTransactions
      .filter(tx => !tx.is_refund)
      .reduce((sum, tx) => sum + parseFloat(tx.total_amount || 0), 0);
    
    const todaySales = today
      .filter(tx => !tx.is_refund)
      .reduce((sum, tx) => sum + parseFloat(tx.total_amount || 0), 0);
    
    const refundAmount = filteredTransactions
      .filter(tx => tx.is_refund)
      .reduce((sum, tx) => sum + Math.abs(parseFloat(tx.total_amount || 0)), 0);
    
    const refundCount = filteredTransactions.filter(tx => tx.is_refund).length;
    
    const averageSale = filteredTransactions.filter(tx => !tx.is_refund).length > 0 
      ? totalSales / filteredTransactions.filter(tx => !tx.is_refund).length 
      : 0;
    
    const pendingTransactions = filteredTransactions.filter(tx => 
      !tx.is_refund && tx.status === 'pending'
    );
    
    const pendingAmount = pendingTransactions.reduce((sum, tx) => sum + parseFloat(tx.total_amount || 0), 0);
    
    const chequeTransactions = filteredTransactions.filter(tx => 
      !tx.is_refund && tx.payment_method === 'cheque'
    );
    
    const pendingChequeTransactions = chequeTransactions.filter(tx => tx.cheque_status === 'pending');
    const bouncedChequeTransactions = chequeTransactions.filter(tx => tx.cheque_status === 'bounced');
    const clearedChequeTransactions = chequeTransactions.filter(tx => tx.cheque_status === 'cleared');
    
    const onlineTransactions = filteredTransactions.filter(tx => 
      !tx.is_refund && ['online', 'mobile', 'bank_transfer'].includes(tx.payment_method)
    );
    
    const pendingOnlineTransactions = onlineTransactions.filter(tx => tx.online_payment_status === 'pending');
    const failedOnlineTransactions = onlineTransactions.filter(tx => tx.online_payment_status === 'failed');
    
    // ============ Credit sale statistics ============
    const creditTransactions = filteredTransactions.filter(tx => 
      !tx.is_refund && tx.payment_method === 'credit'
    );
    
    const pendingCreditTransactions = creditTransactions.filter(tx => tx.status === 'pending');
    const completedCreditTransactions = creditTransactions.filter(tx => tx.status === 'completed');
    const creditTotal = creditTransactions.reduce((sum, tx) => sum + parseFloat(tx.total_amount || 0), 0);
    // ================================================
    
    const bulkSaleTransactions = filteredTransactions.filter(tx => 
      !tx.is_refund && tx.has_bulk_sale === true
    );
    
    const bulkSaleTotal = bulkSaleTransactions.reduce((sum, tx) => sum + parseFloat(tx.total_amount || 0), 0);
    
    return {
      totalTransactions: filteredTransactions.length,
      totalSales,
      todayTransactions: today.length,
      todaySales,
      refundCount,
      refundAmount,
      averageSale,
      pendingTransactions: pendingTransactions.length,
      pendingAmount,
      chequeTransactions: chequeTransactions.length,
      pendingChequeTransactions: pendingChequeTransactions.length,
      bouncedChequeTransactions: bouncedChequeTransactions.length,
      clearedChequeTransactions: clearedChequeTransactions.length,
      onlineTransactions: onlineTransactions.length,
      pendingOnlineTransactions: pendingOnlineTransactions.length,
      failedOnlineTransactions: failedOnlineTransactions.length,
      creditTransactions: creditTransactions.length,
      pendingCreditTransactions: pendingCreditTransactions.length,
      completedCreditTransactions: completedCreditTransactions.length,
      creditTotal,
      bulkSaleTransactions: bulkSaleTransactions.length,
      bulkSaleTotal
    };
  };

  const currentStats = calculateStats();

  // Pagination
  const handleChangePage = (event, newPage) => setPage(newPage);
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedTransactions = filteredTransactions.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const formatCustomDateRangeDisplay = () => {
    if (customStartDate && customEndDate) {
      const start = new Date(customStartDate).toLocaleDateString();
      const end = new Date(customEndDate).toLocaleDateString();
      return `${start} - ${end}`;
    }
    return "Select Dates";
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom fontWeight="bold">
            <ReceiptLongIcon sx={{ mr: 2, verticalAlign: 'middle' }} />
            POS Sales History
          </Typography>
          <Typography variant="body1" color="textSecondary">
            View and manage all POS transactions including cash, card, credit, cheque, online payments, and bulk sales
          </Typography>
        </Box>
        <Box display="flex" gap={1}>
          <Button variant="contained" color="primary" startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/pos')}>New Sale</Button>
          <Button variant="outlined" startIcon={<RefreshIcon />}
            onClick={fetchTransactions} disabled={loading}>Refresh</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}
        action={<Button color="inherit" size="small" onClick={fetchTransactions}>Retry</Button>}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess('')}>{success}</Alert>}

      {/* Stats Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box><Typography color="inherit" variant="body2">Total Sales</Typography>
                  <Typography variant="h4" fontWeight="bold">{formatCurrency(currentStats.totalSales)}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}><AttachMoneyIcon /></Avatar>
              </Box>
              <Typography variant="caption">{currentStats.totalTransactions} transactions</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box><Typography color="inherit" variant="body2">Today's Sales</Typography>
                  <Typography variant="h4" fontWeight="bold">{formatCurrency(currentStats.todaySales)}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}><TimelineIcon /></Avatar>
              </Box>
              <Typography variant="caption">{currentStats.todayTransactions} today</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #fc4a1a 0%, #f7b733 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box><Typography color="inherit" variant="body2">Pending</Typography>
                  <Typography variant="h4" fontWeight="bold">{formatCurrency(currentStats.pendingAmount)}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}><PendingIcon /></Avatar>
              </Box>
              <Typography variant="caption">{currentStats.pendingTransactions} pending</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', background: 'linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%)', color: 'white' }}>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box><Typography color="inherit" variant="body2">Refunds</Typography>
                  <Typography variant="h4" fontWeight="bold">{formatCurrency(currentStats.refundAmount)}</Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}><CancelIcon /></Avatar>
              </Box>
              <Typography variant="caption">{currentStats.refundCount} refunds</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Additional Stats Row - Original Nice Styling */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card variant="outlined" sx={{ height: '100%', bgcolor: '#ffffff' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <ChequeIcon sx={{ color: '#1976D2' }} />
                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#1976D2' }}>Cheque Payments</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#1976D2' }}>{currentStats.chequeTransactions}</Typography>
              <Box display="flex" justifyContent="space-between" mt={1} flexWrap="wrap">
                <Chip label={`Pending: ${currentStats.pendingChequeTransactions}`} size="small" color="warning" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                <Chip label={`Cleared: ${currentStats.clearedChequeTransactions}`} size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                <Chip label={`Bounced: ${currentStats.bouncedChequeTransactions}`} size="small" color="error" variant="outlined" sx={{ fontSize: '0.7rem' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card variant="outlined" sx={{ height: '100%', bgcolor: '#ffffff' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <OnlinePaymentIcon sx={{ color: '#9c27b0' }} />
                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#9c27b0' }}>Online Payments</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#9c27b0' }}>{currentStats.onlineTransactions}</Typography>
              <Box display="flex" justifyContent="space-between" mt={1}>
                <Chip label={`Pending: ${currentStats.pendingOnlineTransactions}`} size="small" color="warning" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                <Chip label={`Failed: ${currentStats.failedOnlineTransactions}`} size="small" color="error" variant="outlined" sx={{ fontSize: '0.7rem' }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card variant="outlined" sx={{ height: '100%', bgcolor: '#ffffff' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <BulkIcon sx={{ color: '#f57c00' }} />
                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#f57c00' }}>Bulk Sales</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#f57c00' }}>{currentStats.bulkSaleTransactions}</Typography>
              <Typography variant="caption" color="textSecondary" display="block">
                Total: {formatCurrency(currentStats.bulkSaleTotal)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card variant="outlined" sx={{ height: '100%', bgcolor: '#ffffff' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <CheckCircleIcon sx={{ color: '#2e7d32' }} />
                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#2e7d32' }}>Credit Sales</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#2e7d32' }}>{currentStats.creditTransactions}</Typography>
              <Box display="flex" justifyContent="space-between" mt={1}>
                <Chip label={`Pending: ${currentStats.pendingCreditTransactions}`} size="small" color="warning" variant="outlined" sx={{ fontSize: '0.7rem' }} />
                <Chip label={`Paid: ${currentStats.completedCreditTransactions}`} size="small" color="success" variant="outlined" sx={{ fontSize: '0.7rem' }} />
              </Box>
              <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                Total: {formatCurrency(currentStats.creditTotal)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={2.4}>
          <Card variant="outlined" sx={{ height: '100%', bgcolor: '#ffffff' }}>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
                <ReceiptIcon sx={{ color: '#9e9e9e' }} />
                <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#616161' }}>Transactions</Typography>
              </Box>
              <Typography variant="h5" fontWeight="bold" sx={{ color: '#616161' }}>{currentStats.totalTransactions}</Typography>
              <Typography variant="caption" color="textSecondary">total transactions</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters Section */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField fullWidth placeholder="Search receipt, customer, product..."
              value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment> }}
              size="small" variant="outlined" />
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Date Range</InputLabel>
              <Select value={dateFilter} onChange={(e) => { setDateFilter(e.target.value);
                  if (e.target.value !== 'custom') { setCustomStartDate(''); setCustomEndDate(''); } }}>
                <MenuItem value="all">All Dates</MenuItem>
                <MenuItem value="today">Today</MenuItem>
                <MenuItem value="yesterday">Yesterday</MenuItem>
                <MenuItem value="week">Last 7 Days</MenuItem>
                <MenuItem value="month">This Month</MenuItem>
                <MenuItem value="custom">Custom Range</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <Button fullWidth variant="outlined" startIcon={<CalendarIcon />}
              onClick={handleDateRangeClick} sx={{ height: '40px', justifyContent: 'flex-start' }}>
              <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                <span>Custom Dates</span>
                {dateFilter === 'custom' && (customStartDate || customEndDate) && (
                  <Chip label={formatCustomDateRangeDisplay()} size="small" color="primary"
                    sx={{ ml: 1, fontSize: '0.7rem' }} onDelete={handleClearCustomDateRange} />
                )}
              </Box>
            </Button>
          </Grid>
          
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Method</InputLabel>
              <Select value={paymentFilter} onChange={(e) => setPaymentFilter(e.target.value)}>
                <MenuItem value="all">All Methods</MenuItem>
                <MenuItem value="cash">Cash</MenuItem>
                <MenuItem value="card">Card</MenuItem>
                <MenuItem value="credit">Credit</MenuItem>
                <MenuItem value="cheque">Cheque</MenuItem>
                <MenuItem value="online">Online</MenuItem>
                <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Autocomplete size="small" options={customers}
              getOptionLabel={(option) => `${option.name}${option.phone ? ` (${option.phone})` : ''}`}
              value={selectedCustomer} onChange={(event, newValue) => setSelectedCustomer(newValue)}
              renderInput={(params) => <TextField {...params} label="Filter by Customer" variant="outlined" />}
              isOptionEqualToValue={(option, value) => option?.id === value?.id} />
          </Grid>

          <Grid item xs={12}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mt={1}>
              <Button startIcon={<FilterIcon />} onClick={() => setExpandedFilters(!expandedFilters)} size="small">
                {expandedFilters ? 'Hide' : 'Show'} Advanced Filters
              </Button>
              <Typography variant="body2" color="textSecondary">
                <strong>{filteredTransactions.length}</strong> transactions found
                {currentStats.bulkSaleTransactions > 0 && (
                  <span> | Bulk Sales: {currentStats.bulkSaleTransactions}</span>
                )}
              </Typography>
              <Box display="flex" gap={1}>
                <Button variant="contained" color="primary" startIcon={<DownloadIcon />}
                  onClick={handleExportCSV} disabled={filteredTransactions.length === 0} size="small">Export CSV</Button>
                <Button variant="outlined" startIcon={<FilterIcon />} onClick={() => {
                  setSearchTerm(''); setDateFilter('all'); setPaymentFilter('all'); setStatusFilter('all');
                  setChequeStatusFilter('all'); setOnlineStatusFilter('all'); setBulkSaleFilter('all');
                  setSelectedCustomer(null); setCustomStartDate(''); setCustomEndDate('');
                }} size="small">Clear Filters</Button>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Collapse in={expandedFilters}>
              <Paper variant="outlined" sx={{ p: 2, mt: 2, bgcolor: 'grey.50' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Status</InputLabel>
                      <Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                        <MenuItem value="all">All Status</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                        <MenuItem value="refunded">Refunded</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Cheque Status</InputLabel>
                      <Select value={chequeStatusFilter} onChange={(e) => setChequeStatusFilter(e.target.value)}>
                        <MenuItem value="all">All Cheque Status</MenuItem>
                        <MenuItem value="pending">Pending Clearance</MenuItem>
                        <MenuItem value="cleared">Cleared</MenuItem>
                        <MenuItem value="bounced">Bounced</MenuItem>
                        <MenuItem value="cancelled">Cancelled</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Online Status</InputLabel>
                      <Select value={onlineStatusFilter} onChange={(e) => setOnlineStatusFilter(e.target.value)}>
                        <MenuItem value="all">All Online Status</MenuItem>
                        <MenuItem value="pending">Pending</MenuItem>
                        <MenuItem value="completed">Completed</MenuItem>
                        <MenuItem value="failed">Failed</MenuItem>
                        <MenuItem value="refunded">Refunded</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <FormControl fullWidth size="small">
                      <InputLabel>Bulk Sale</InputLabel>
                      <Select value={bulkSaleFilter} onChange={(e) => setBulkSaleFilter(e.target.value)}>
                        <MenuItem value="all">All Transactions</MenuItem>
                        <MenuItem value="yes">Bulk Sales Only</MenuItem>
                        <MenuItem value="no">Regular Sales Only</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </Paper>
            </Collapse>
          </Grid>
        </Grid>
      </Paper>

      {/* Custom Date Range Popover */}
      <Popover open={Boolean(dateRangeAnchorEl)} anchorEl={dateRangeAnchorEl} onClose={handleDateRangeClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { p: 3, width: 400 } }}>
        <Typography variant="h6" gutterBottom>Select Custom Date Range</Typography>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField fullWidth label="Start Date" type="date" value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small" variant="outlined" />
          </Grid>
          <Grid item xs={12}>
            <TextField fullWidth label="End Date" type="date" value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small" variant="outlined" />
          </Grid>
        </Grid>
        <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
          <Button onClick={handleClearCustomDateRange} startIcon={<ClearIcon />} color="inherit">Clear</Button>
          <Box display="flex" gap={1}>
            <Button onClick={handleDateRangeClose} color="inherit">Cancel</Button>
            <Button onClick={handleApplyCustomDateRange} variant="contained"
              disabled={!customStartDate || !customEndDate}>Apply Filter</Button>
          </Box>
        </Box>
      </Popover>

      {/* ============ Credit Payment Completion Dialog ============ */}
      <Dialog open={creditPaymentDialogOpen} onClose={() => setCreditPaymentDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box display="flex" alignItems="center" gap={1}>
              <WalletIcon sx={{ color: '#f57c00' }} />
              <Typography variant="h6" fontWeight="bold">Complete Credit Payment</Typography>
            </Box>
            <IconButton onClick={() => setCreditPaymentDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {selectedTransactionForMenu && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="info" sx={{ mb: 3 }}>
                <Typography variant="body2">
                  <strong>Transaction:</strong> {selectedTransactionForMenu.pos_number}<br />
                  <strong>Customer:</strong> {selectedTransactionForMenu.customer_name}<br />
                  <strong>Amount:</strong> {formatCurrency(selectedTransactionForMenu.total_amount)}
                </Typography>
              </Alert>

              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                How did the customer pay?
              </Typography>

              <FormControl component="fieldset" sx={{ mb: 3, width: '100%' }}>
                <RadioGroup
                  value={creditPaymentData.paymentMethod}
                  onChange={(e) => setCreditPaymentData({...creditPaymentData, paymentMethod: e.target.value})}
                >
                  <Grid container spacing={1}>
                    <Grid item xs={6}>
                      <Paper 
                        variant={creditPaymentData.paymentMethod === 'cash' ? 'outlined' : 'outlined'}
                        sx={{ 
                          p: 1.5, 
                          cursor: 'pointer',
                          bgcolor: creditPaymentData.paymentMethod === 'cash' ? '#e8f5e8' : 'transparent',
                          borderColor: creditPaymentData.paymentMethod === 'cash' ? '#2e7d32' : '#ddd',
                          borderWidth: creditPaymentData.paymentMethod === 'cash' ? '2px' : '1px'
                        }}
                        onClick={() => setCreditPaymentData({...creditPaymentData, paymentMethod: 'cash'})}
                      >
                        <FormControlLabel 
                          value="cash" 
                          control={<Radio />} 
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                              <CashIcon sx={{ color: '#2e7d32' }} />
                              <Typography>Cash</Typography>
                            </Box>
                          } 
                          sx={{ width: '100%', m: 0 }}
                        />
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper 
                        variant={creditPaymentData.paymentMethod === 'card' ? 'outlined' : 'outlined'}
                        sx={{ 
                          p: 1.5, 
                          cursor: 'pointer',
                          bgcolor: creditPaymentData.paymentMethod === 'card' ? '#e3f2fd' : 'transparent',
                          borderColor: creditPaymentData.paymentMethod === 'card' ? '#1976d2' : '#ddd',
                          borderWidth: creditPaymentData.paymentMethod === 'card' ? '2px' : '1px'
                        }}
                        onClick={() => setCreditPaymentData({...creditPaymentData, paymentMethod: 'card'})}
                      >
                        <FormControlLabel 
                          value="card" 
                          control={<Radio />} 
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                              <CreditCardIcon sx={{ color: '#1976d2' }} />
                              <Typography>Card</Typography>
                            </Box>
                          } 
                          sx={{ width: '100%', m: 0 }}
                        />
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper 
                        variant={creditPaymentData.paymentMethod === 'online' ? 'outlined' : 'outlined'}
                        sx={{ 
                          p: 1.5, 
                          cursor: 'pointer',
                          bgcolor: creditPaymentData.paymentMethod === 'online' ? '#f3e5f5' : 'transparent',
                          borderColor: creditPaymentData.paymentMethod === 'online' ? '#9c27b0' : '#ddd',
                          borderWidth: creditPaymentData.paymentMethod === 'online' ? '2px' : '1px'
                        }}
                        onClick={() => setCreditPaymentData({...creditPaymentData, paymentMethod: 'online'})}
                      >
                        <FormControlLabel 
                          value="online" 
                          control={<Radio />} 
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                              <OnlinePaymentIcon sx={{ color: '#9c27b0' }} />
                              <Typography>Online</Typography>
                            </Box>
                          } 
                          sx={{ width: '100%', m: 0 }}
                        />
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper 
                        variant={creditPaymentData.paymentMethod === 'bank_transfer' ? 'outlined' : 'outlined'}
                        sx={{ 
                          p: 1.5, 
                          cursor: 'pointer',
                          bgcolor: creditPaymentData.paymentMethod === 'bank_transfer' ? '#e0f2f1' : 'transparent',
                          borderColor: creditPaymentData.paymentMethod === 'bank_transfer' ? '#009688' : '#ddd',
                          borderWidth: creditPaymentData.paymentMethod === 'bank_transfer' ? '2px' : '1px'
                        }}
                        onClick={() => setCreditPaymentData({...creditPaymentData, paymentMethod: 'bank_transfer'})}
                      >
                        <FormControlLabel 
                          value="bank_transfer" 
                          control={<Radio />} 
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                              <WalletIcon sx={{ color: '#009688' }} />
                              <Typography>Bank Transfer</Typography>
                            </Box>
                          } 
                          sx={{ width: '100%', m: 0 }}
                        />
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper 
                        variant={creditPaymentData.paymentMethod === 'mobile' ? 'outlined' : 'outlined'}
                        sx={{ 
                          p: 1.5, 
                          cursor: 'pointer',
                          bgcolor: creditPaymentData.paymentMethod === 'mobile' ? '#e8eaf6' : 'transparent',
                          borderColor: creditPaymentData.paymentMethod === 'mobile' ? '#3f51b5' : '#ddd',
                          borderWidth: creditPaymentData.paymentMethod === 'mobile' ? '2px' : '1px'
                        }}
                        onClick={() => setCreditPaymentData({...creditPaymentData, paymentMethod: 'mobile'})}
                      >
                        <FormControlLabel 
                          value="mobile" 
                          control={<Radio />} 
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                              <MobileIcon sx={{ color: '#3f51b5' }} />
                              <Typography>Mobile</Typography>
                            </Box>
                          } 
                          sx={{ width: '100%', m: 0 }}
                        />
                      </Paper>
                    </Grid>
                    <Grid item xs={6}>
                      <Paper 
                        variant={creditPaymentData.paymentMethod === 'cheque' ? 'outlined' : 'outlined'}
                        sx={{ 
                          p: 1.5, 
                          cursor: 'pointer',
                          bgcolor: creditPaymentData.paymentMethod === 'cheque' ? '#fff3e0' : 'transparent',
                          borderColor: creditPaymentData.paymentMethod === 'cheque' ? '#f57c00' : '#ddd',
                          borderWidth: creditPaymentData.paymentMethod === 'cheque' ? '2px' : '1px'
                        }}
                        onClick={() => setCreditPaymentData({...creditPaymentData, paymentMethod: 'cheque'})}
                      >
                        <FormControlLabel 
                          value="cheque" 
                          control={<Radio />} 
                          label={
                            <Box display="flex" alignItems="center" gap={1}>
                              <ChequeIcon sx={{ color: '#f57c00' }} />
                              <Typography>Cheque</Typography>
                            </Box>
                          } 
                          sx={{ width: '100%', m: 0 }}
                        />
                      </Paper>
                    </Grid>
                  </Grid>
                </RadioGroup>
              </FormControl>

              <TextField
                fullWidth
                label="Reference Number (Optional)"
                value={creditPaymentData.reference}
                onChange={(e) => setCreditPaymentData({...creditPaymentData, reference: e.target.value})}
                placeholder="e.g., Transaction ID, Cheque No, etc."
                size="small"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Notes (Optional)"
                value={creditPaymentData.notes}
                onChange={(e) => setCreditPaymentData({...creditPaymentData, notes: e.target.value})}
                placeholder="Any additional notes"
                multiline
                rows={2}
                size="small"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreditPaymentDialogOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleCreditPaymentCompletion} 
            variant="contained" 
            color="primary"
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : <DoneAllIcon />}
          >
            {loading ? 'Processing...' : 'Confirm Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main Table */}
      <Paper sx={{ width: '100%', overflow: 'hidden', borderRadius: 2, boxShadow: 2 }}>
        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}><CircularProgress /><Typography sx={{ mt: 2 }}>Loading transactions...</Typography></Box>
        ) : filteredTransactions.length === 0 ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <ReceiptIcon sx={{ fontSize: 60, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="textSecondary" gutterBottom>No transactions found</Typography>
            <Button variant="contained" startIcon={<ArrowBackIcon />} onClick={() => navigate('/pos')}>Go to POS</Button>
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Receipt #</strong></TableCell>
                    <TableCell><strong>Date</strong></TableCell>
                    <TableCell><strong>Customer</strong></TableCell>
                    <TableCell><strong>Items</strong></TableCell>
                    <TableCell align="right"><strong>Amount</strong></TableCell>
                    <TableCell><strong>Payment</strong></TableCell>
                    <TableCell><strong>Status</strong></TableCell>
                    <TableCell><strong>Details</strong></TableCell>
                    <TableCell><strong>Actions</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedTransactions.map((transaction) => {
                    const paymentStatus = getPaymentStatusDetails(transaction);
                    const hasBulk = transaction.has_bulk_sale === true;
                    
                    return (
                      <TableRow key={transaction.id} hover>
                        <TableCell>
                          <Box>
                            <Typography variant="body2" fontWeight="bold" color="primary">
                              {transaction.pos_number}
                            </Typography>
                            {hasBulk && (
                              <Chip label="BULK" size="small" sx={{ mt: 0.5, fontSize: '0.6rem', height: 16,
                                bgcolor: '#f57c00', color: 'white', fontWeight: 'bold' }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{formatDate(transaction.transaction_date)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <PersonIcon fontSize="small" color="action" />
                            <Typography variant="body2">{transaction.customer_name}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            <ShoppingCartIcon fontSize="small" color="action" />
                            <Chip label={`${transaction.items?.length || 0} items`} size="small" color="primary" variant="outlined" />
                            {hasBulk && (
                              <Chip label="Bulk" size="small" sx={{ bgcolor: '#f57c00', color: 'white' }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold" color="success.main">
                            {formatCurrency(transaction.total_amount)}
                          </Typography>
                          {transaction.is_refund && (
                            <Typography variant="caption" color="error">(Refund)</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box display="flex" alignItems="center" gap={1}>
                            {paymentIcons[transaction.payment_method] || <PaymentIcon />}
                            <Chip label={transaction.payment_method?.toUpperCase() || 'CASH'}
                              size="small" color={paymentColors[transaction.payment_method] || 'default'} variant="outlined" />
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip label={paymentStatus.label} size="small" color={paymentStatus.color}
                            icon={paymentStatus.icon} sx={{ fontWeight: 'bold' }} />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ maxWidth: 200 }}>
                            {hasBulk && (
                              <Typography variant="caption" sx={{ color: '#f57c00' }} display="block" fontWeight="bold">
                                <BulkIcon fontSize="inherit" /> Bulk - {transaction.bulk_discount_percentage || 0}% off
                              </Typography>
                            )}
                            {transaction.payment_method === 'cheque' && transaction.cheque_details && (
                              <Box>
                                <Typography variant="caption" color="info.main" display="block" fontWeight="bold">
                                  <ChequeIcon fontSize="inherit" /> Chq: {transaction.cheque_details.chequeNumber}
                                </Typography>
                                <Typography variant="caption" color="textSecondary" display="block">
                                  Bank: {transaction.cheque_details.bankName}
                                </Typography>
                                {transaction.cheque_status === 'pending' && transaction.cheque_details.dueDate && (
                                  <Typography variant="caption" color="warning.main" display="block">
                                    Due: {formatDateOnly(transaction.cheque_details.dueDate)}
                                  </Typography>
                                )}
                                {transaction.cheque_status === 'bounced' && (
                                  <Typography variant="caption" color="error.main" fontWeight="bold">⚠️ BOUNCED</Typography>
                                )}
                                {transaction.cheque_status === 'cleared' && (
                                  <Typography variant="caption" color="success.main" fontWeight="bold">✓ CLEARED</Typography>
                                )}
                              </Box>
                            )}
                            {['online', 'mobile', 'bank_transfer'].includes(transaction.payment_method) && (
                              <Typography variant="caption" color="secondary.main" display="block">
                                Ref: {transaction.online_payment_reference || 'N/A'}
                              </Typography>
                            )}
                            {transaction.payment_method === 'credit' && transaction.status === 'completed' && transaction.settlement_method && (
                              <Typography variant="caption" color="success.main" display="block" fontWeight="bold">
                                ✓ Paid via {transaction.settlement_method.toUpperCase()}
                                {transaction.settlement_reference && ` (Ref: ${transaction.settlement_reference})`}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box display="flex" gap={0.5}>
                            <Tooltip title="View">
                              <IconButton size="small" onClick={() => handleViewTransaction(transaction)} color="primary">
                                <ViewIcon />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Print">
                              <IconButton size="small" onClick={() => handlePrintReceipt(transaction)} color="secondary">
                                <PrintIcon />
                              </IconButton>
                            </Tooltip>
                            {!transaction.is_refund && transaction.status === 'pending' && (
                              <Tooltip title="Update Status">
                                <IconButton size="small" onClick={(e) => handleOpenMenu(e, transaction)} color="warning">
                                  <MoreVertIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination rowsPerPageOptions={[10, 25, 50, 100]} component="div"
              count={filteredTransactions.length} rowsPerPage={rowsPerPage} page={page}
              onPageChange={handleChangePage} onRowsPerPageChange={handleChangeRowsPerPage} />
          </>
        )}
      </Paper>

      {/* Status Update Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleCloseMenu}
        PaperProps={{ elevation: 3, sx: { minWidth: 250 } }}>
        {selectedTransactionForMenu && selectedTransactionForMenu.status === 'pending' && (
          <Box>
            {selectedTransactionForMenu.payment_method === 'credit' && (
              <MenuItem onClick={handleOpenCreditPaymentDialog} disabled={loading}>
                <ListItemIcon><DoneAllIcon color="success" /></ListItemIcon>
                <ListItemText primary="Mark as Completed" secondary="Record how customer paid" />
              </MenuItem>
            )}
            {selectedTransactionForMenu.payment_method === 'cheque' && (
              <Box>
                <MenuItem onClick={() => updateChequeStatus(selectedTransactionForMenu.id, 'cleared', 'Cheque cleared')} disabled={loading}>
                  <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Mark as Cleared" secondary="Cheque cleared" />
                </MenuItem>
                <MenuItem onClick={() => updateChequeStatus(selectedTransactionForMenu.id, 'bounced', 'Cheque bounced')} disabled={loading}>
                  <ListItemIcon><ErrorOutlineIcon color="error" /></ListItemIcon>
                  <ListItemText primary="Mark as Bounced" secondary="Cheque bounced" />
                </MenuItem>
                <MenuItem onClick={() => updateChequeStatus(selectedTransactionForMenu.id, 'cancelled', 'Cheque cancelled')} disabled={loading}>
                  <ListItemIcon><CancelIcon color="error" /></ListItemIcon>
                  <ListItemText primary="Mark as Cancelled" secondary="Cheque cancelled" />
                </MenuItem>
              </Box>
            )}
            {['online', 'mobile', 'bank_transfer'].includes(selectedTransactionForMenu.payment_method) && (
              <Box>
                <MenuItem onClick={() => updateOnlinePaymentStatus(selectedTransactionForMenu.id, 'completed', 'Payment confirmed')} disabled={loading}>
                  <ListItemIcon><CheckCircleIcon color="success" /></ListItemIcon>
                  <ListItemText primary="Mark as Completed" secondary="Payment confirmed" />
                </MenuItem>
                <MenuItem onClick={() => updateOnlinePaymentStatus(selectedTransactionForMenu.id, 'failed', 'Payment failed')} disabled={loading}>
                  <ListItemIcon><ErrorOutlineIcon color="error" /></ListItemIcon>
                  <ListItemText primary="Mark as Failed" secondary="Payment failed" />
                </MenuItem>
              </Box>
            )}
            <Divider />
          </Box>
        )}
        <MenuItem onClick={() => { handleViewTransaction(selectedTransactionForMenu); handleCloseMenu(); }}>
          <ListItemIcon><ViewIcon color="info" /></ListItemIcon>
          <ListItemText primary="View Details" />
        </MenuItem>
      </Menu>

      {/* Transaction Details Dialog */}
      <Dialog open={viewDialogOpen} onClose={() => setViewDialogOpen(false)} maxWidth="md" fullWidth scroll="paper">
        {selectedTransaction && (
          <>
            <DialogTitle>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h6" fontWeight="bold">{selectedTransaction.pos_number}</Typography>
                  <Typography variant="body2" color="textSecondary">{formatDate(selectedTransaction.transaction_date)}</Typography>
                  {selectedTransaction.has_bulk_sale && (
                    <Chip label="BULK SALE" size="small" sx={{ mt: 1, bgcolor: '#f57c00', color: 'white' }} icon={<BulkIcon />} />
                  )}
                </Box>
                <Chip label={getPaymentStatusDetails(selectedTransaction).label}
                  color={getPaymentStatusDetails(selectedTransaction).color}
                  icon={getPaymentStatusDetails(selectedTransaction).icon} sx={{ fontWeight: 'bold' }} />
              </Box>
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={3} sx={{ mb: 3 }}>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      <StoreIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Transaction Information
                    </Typography>
                    <Stack spacing={1}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Cashier:</Typography>
                        <Typography variant="body2" fontWeight="medium">{selectedTransaction.user_name || 'POS User'}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Payment Method:</Typography>
                        <Box display="flex" alignItems="center" gap={1}>
                          {paymentIcons[selectedTransaction.payment_method] || <PaymentIcon />}
                          <Typography variant="body2" fontWeight="medium">{selectedTransaction.payment_method?.toUpperCase()}</Typography>
                        </Box>
                      </Box>
                      {selectedTransaction.payment_method === 'credit' && selectedTransaction.status === 'completed' && selectedTransaction.settlement_method && (
                        <Box display="flex" justifyContent="space-between">
                          <Typography variant="body2">Settlement Method:</Typography>
                          <Typography variant="body2" fontWeight="medium" color="success.main">
                            {selectedTransaction.settlement_method.toUpperCase()}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper variant="outlined" sx={{ p: 2 }}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      <PersonIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                      Customer Information
                    </Typography>
                    <Stack spacing={1}>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Customer:</Typography>
                        <Typography variant="body2" fontWeight="medium">{selectedTransaction.customer_name}</Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2">Customer ID:</Typography>
                        <Typography variant="body2" fontWeight="medium">{selectedTransaction.customer_id}</Typography>
                      </Box>
                    </Stack>
                  </Paper>
                </Grid>
              </Grid>

              {selectedTransaction.has_bulk_sale && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#fff3e0', borderColor: '#f57c00' }}>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <BulkIcon sx={{ color: '#f57c00' }} />
                    <Typography variant="subtitle1" fontWeight="bold" sx={{ color: '#f57c00' }}>Bulk Sale Package</Typography>
                  </Box>
                  <Typography variant="body2">
                    This transaction includes bulk sale items with package discount of <strong>{selectedTransaction.bulk_discount_percentage || 0}%</strong>
                  </Typography>
                </Paper>
              )}

              {selectedTransaction.payment_method === 'cheque' && selectedTransaction.cheque_details && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#fff3e0' }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    <ChequeIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle' }} />
                    Cheque Payment Details
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Cheque Number:</strong> {selectedTransaction.cheque_details.chequeNumber}</Typography>
                      <Typography variant="body2"><strong>Bank:</strong> {selectedTransaction.cheque_details.bankName}</Typography>
                      <Typography variant="body2"><strong>Cheque Date:</strong> {formatDateOnly(selectedTransaction.cheque_details.chequeDate)}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Due Date:</strong> {formatDateOnly(selectedTransaction.cheque_details.dueDate)}</Typography>
                      <Typography variant="body2"><strong>Type:</strong> {selectedTransaction.cheque_details.isPostDated ? 'Post-dated' : 'Regular'}</Typography>
                      <Box display="flex" alignItems="center" gap={1} mt={1}>
                        <Typography variant="body2"><strong>Status:</strong></Typography>
                        <Chip label={selectedTransaction.cheque_status?.toUpperCase() || 'PENDING'} size="small"
                          color={selectedTransaction.cheque_status === 'cleared' ? 'success' :
                                selectedTransaction.cheque_status === 'pending' ? 'warning' :
                                selectedTransaction.cheque_status === 'bounced' ? 'error' : 'default'} />
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {selectedTransaction.payment_method === 'credit' && selectedTransaction.status === 'completed' && (
                <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#e8f5e8', borderColor: '#2e7d32' }}>
                  <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                    <CheckCircleIcon fontSize="small" sx={{ mr: 1, verticalAlign: 'middle', color: '#2e7d32' }} />
                    Credit Payment Settlement
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Settlement Method:</strong> {selectedTransaction.settlement_method?.toUpperCase() || 'N/A'}</Typography>
                      <Typography variant="body2"><strong>Settlement Date:</strong> {selectedTransaction.settlement_date ? formatDateOnly(selectedTransaction.settlement_date) : 'N/A'}</Typography>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Typography variant="body2"><strong>Reference:</strong> {selectedTransaction.settlement_reference || 'N/A'}</Typography>
                      {selectedTransaction.notes && (
                        <Typography variant="body2"><strong>Notes:</strong> {selectedTransaction.notes}</Typography>
                      )}
                    </Grid>
                  </Grid>
                </Paper>
              )}

              <Typography variant="h6" gutterBottom>
                Items Purchased ({selectedTransaction.items?.length || 0})
                {selectedTransaction.has_bulk_sale && (
                  <Chip label={`Bulk Discount: ${selectedTransaction.bulk_discount_percentage || 0}%`} size="small"
                    sx={{ ml: 2, bgcolor: '#f57c00', color: 'white', fontWeight: 'bold' }} />
                )}
              </Typography>
              
              <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product</TableCell>
                      <TableCell align="center">Qty</TableCell>
                      <TableCell align="right">Unit Price</TableCell>
                      <TableCell align="right">Discount</TableCell>
                      <TableCell align="right">Total</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedTransaction.items?.map((item, index) => {
                      const isBulkItem = item.is_bulk_sale === true || 
                                        item.is_bulk_sale === 'true' || 
                                        item.is_bulk_sale === 1 || 
                                        item.is_bulk_sale === '1';
                      return (
                        <TableRow key={index}>
                          <TableCell>
                            <Box>
                              <Typography variant="body2" fontWeight="medium">{item.product_name}</Typography>
                              <Typography variant="caption" color="textSecondary">{item.product_code}</Typography>
                              {isBulkItem && (
                                <Chip label="BULK" size="small" sx={{ mt: 0.5, bgcolor: '#f57c00', color: 'white', fontSize: '0.6rem' }} />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="center">{item.quantity}</TableCell>
                          <TableCell align="right">{formatCurrency(item.unit_price)}</TableCell>
                          <TableCell align="right">
                            {item.discount_percentage > 0 ? (
                              <Typography variant="body2" color="error">{item.discount_percentage}%</Typography>
                            ) : isBulkItem ? (
                              <Typography variant="body2" sx={{ color: '#f57c00' }}>Bulk</Typography>
                            ) : '-'}
                          </TableCell>
                          <TableCell align="right"><strong>{formatCurrency(item.line_total)}</strong></TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>Financial Summary</Typography>
                <Stack spacing={1}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2">Subtotal:</Typography>
                    <Typography variant="body2">{formatCurrency(selectedTransaction.subtotal)}</Typography>
                  </Box>
                  {selectedTransaction.discount_amount > 0 && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="error">Discount:</Typography>
                      <Typography variant="body2" color="error">-{formatCurrency(selectedTransaction.discount_amount)}</Typography>
                    </Box>
                  )}
                  {selectedTransaction.tax_amount > 0 && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2">Tax:</Typography>
                      <Typography variant="body2">{formatCurrency(selectedTransaction.tax_amount)}</Typography>
                    </Box>
                  )}
                  <Divider />
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body1" fontWeight="bold">Total Amount:</Typography>
                    <Typography variant="body1" fontWeight="bold" color="primary">
                      {formatCurrency(selectedTransaction.total_amount)}
                    </Typography>
                  </Box>
                  <Box display="flex" justifyContent="space-between" mt={2}>
                    <Typography variant="body2">Payment Status:</Typography>
                    <Chip label={getPaymentStatusDetails(selectedTransaction).label}
                      size="small" color={getPaymentStatusDetails(selectedTransaction).color}
                      icon={getPaymentStatusDetails(selectedTransaction).icon} />
                  </Box>
                </Stack>
              </Paper>
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setViewDialogOpen(false)} color="inherit">Close</Button>
              <Button variant="contained" color="primary" startIcon={<PrintIcon />}
                onClick={() => handlePrintReceipt(selectedTransaction)}>Print Receipt</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </Container>
  );
};

export default POSSales;